// frontend/app/components/lessonPlans/authoring/WizardStepDetails.js
//
// Step 1: what the lesson is, and which topic it hangs off.

import React from "react";
import { StyleSheet, Text, View } from "react-native";

import TopicAnchorPicker from "./TopicAnchorPicker";
import {
  Field,
  SectionCard,
  StringListField,
  TextField,
} from "./WizardFields";

const WizardStepDetails = ({
  colors,
  draft,
  onChange,
  errors = {},
  curriculum = [],
  isEditing = false,
  t,
}) => {
  const translate = t || ((key) => key);
  const showError = (message) => (message ? translate(message) : message);
  const set = (patch) => onChange({ ...draft, ...patch });

  return (
    <View>
      <SectionCard
        colors={colors}
        title={translate("Lesson details")}
        subtitle={translate("What a teacher sees on the lesson card and cover page.")}
        icon="text-box-outline"
      >
        <Field
          colors={colors}
          label={translate("Title")}
          required
          error={showError(errors.title)}
        >
          <TextField
            colors={colors}
            value={draft.title}
            onChange={(title) => set({ title })}
            placeholder={translate("Lesson title")}
            error={showError(errors.title)}
          />
        </Field>

        <Field
          colors={colors}
          label={translate("Web address")}
          required
          error={showError(errors.slug)}
          hint={
            isEditing
              ? translate("The address cannot change once a lesson exists.")
              : translate("Lowercase, dashes instead of spaces. Must be unique.")
          }
        >
          <TextField
            colors={colors}
            value={draft.slug}
            onChange={(slug) => set({ slug })}
            placeholder="my-lesson-title"
            error={showError(errors.slug)}
          />
        </Field>

        <Field colors={colors} label={translate("Subtitle")}>
          <TextField
            colors={colors}
            value={draft.subtitle}
            onChange={(subtitle) => set({ subtitle })}
            placeholder={translate("Curriculum code or reference")}
          />
        </Field>

        <Field
          colors={colors}
          label={translate("Description")}
          hint={translate("One or two sentences, shown at the top of the cover page.")}
        >
          <TextField
            colors={colors}
            value={draft.description}
            onChange={(description) => set({ description })}
            placeholder={translate("What this lesson covers…")}
            multiline
          />
        </Field>

        <Field
          colors={colors}
          label={translate("Lesson number")}
          error={showError(errors.lesson_no)}
        >
          <TextField
            colors={colors}
            value={draft.lesson_no}
            onChange={(lesson_no) => set({ lesson_no })}
            placeholder="1"
            keyboardType="numeric"
            error={showError(errors.lesson_no)}
          />
        </Field>
      </SectionCard>

      <SectionCard
        colors={colors}
        title={translate("Curriculum anchor")}
        subtitle={translate(
          "Which topic this lesson belongs to. Asset references resolve against it."
        )}
        icon="file-tree-outline"
      >
        {errors.board_topic_id ? (
          <Text style={[styles.anchorError, { color: colors.danger }]}>
            {showError(errors.board_topic_id)}
          </Text>
        ) : null}
        <TopicAnchorPicker
          colors={colors}
          curriculum={curriculum}
          value={draft}
          onChange={(anchor) => set(anchor)}
          t={t}
        />
      </SectionCard>

      <SectionCard
        colors={colors}
        title={translate("Learning objectives")}
        subtitle={translate("By the end of the lesson, students will be able to…")}
        icon="target"
      >
        <StringListField
          colors={colors}
          items={draft.objectives}
          onChange={(objectives) => set({ objectives })}
          placeholder={translate("Describe what students will be able to do.")}
          addLabel="Add objective"
          ordered
          t={t}
        />
        {errors.objectives ? (
          <Text style={[styles.anchorError, { color: colors.danger }]}>
            {showError(errors.objectives)}
          </Text>
        ) : null}
      </SectionCard>

      <SectionCard
        colors={colors}
        title={translate("Prerequisite knowledge")}
        icon="stairs-up"
        accent={colors.info}
      >
        <StringListField
          colors={colors}
          items={draft.prerequisites}
          onChange={(prerequisites) => set({ prerequisites })}
          placeholder={translate("What students should already know.")}
          addLabel="Add prerequisite"
          t={t}
        />
      </SectionCard>

      <SectionCard
        colors={colors}
        title={translate("Toolkit")}
        subtitle={translate("What the teacher needs to hand.")}
        icon="toolbox-outline"
        accent={colors.success}
      >
        <StringListField
          colors={colors}
          items={draft.materials}
          onChange={(materials) => set({ materials })}
          placeholder={translate("Interactive PWO (3D model and simulations)")}
          addLabel="Add item"
          t={t}
        />
      </SectionCard>

      <SectionCard
        colors={colors}
        title={translate("Teaching tips")}
        subtitle={translate("Teacher-only. Never shown to students.")}
        icon="lightbulb-on-outline"
        accent={colors.warning}
      >
        <StringListField
          colors={colors}
          items={draft.teacher_tips}
          onChange={(teacher_tips) => set({ teacher_tips })}
          placeholder={translate("Use the 3D model before introducing terminology.")}
          addLabel="Add tip"
          ordered
          t={t}
        />
      </SectionCard>
    </View>
  );
};

const styles = StyleSheet.create({
  anchorError: { fontSize: 11, fontWeight: "600", marginBottom: 8 },
});

export default WizardStepDetails;
